/**
 * Markdown Content
 *
 * Renders untrusted markdown (e.g. imported ticket text) with the app's
 * typography. Raw HTML is intentionally not rendered.
 */

import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';

const components: Components = {
  a: ({ href, title, children }) => (
    <Link href={href} title={title} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </Link>
  ),
};

interface MarkdownContentProps {
  children: string;
}

export function MarkdownContent({ children }: MarkdownContentProps) {
  return (
    <Box
      sx={(theme) => ({
        ...theme.typography.body1,
        lineHeight: 1.7,
        color: 'text.primary',
        overflowWrap: 'anywhere',
        // Sibling-based spacing, so the first/last block never adds outer margin.
        '& :is(p, ul, ol, pre, blockquote, table, hr, h1, h2, h3, h4, h5, h6)': { my: 0 },
        '& * + :is(p, ul, ol, pre, blockquote, table)': { mt: 1.5 },
        '& * + :is(h1, h2, h3, h4, h5, h6), & * + hr, & hr + *': { mt: 3 },
        '& :is(h1, h2, h3, h4, h5, h6) + *': { mt: 1 },
        // Ticket bodies sit below the page's own headings, so keep theirs small.
        '& :is(h1, h2, h3, h4, h5, h6)': {
          ...theme.typography.subtitle1,
          fontSize: '1rem',
        },
        '& ul, & ol': { pl: 3 },
        '& li + li, & li > :is(ul, ol)': { mt: 0.5 },
        '& li > p + p': { mt: 0.5 },
        '& code': {
          fontFamily: '"Cascadia Code", Consolas, "Courier New", monospace',
          fontSize: '0.875em',
          bgcolor: 'surfaceContainerHigh',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
        },
        '& pre': {
          bgcolor: 'surfaceContainerHigh',
          p: 2,
          borderRadius: 1,
          overflowX: 'auto',
          '& code': { bgcolor: 'transparent', p: 0, fontSize: '0.8125rem' },
        },
        '& blockquote': {
          borderLeft: 3,
          borderColor: 'outlineVariant',
          color: 'text.secondary',
          pl: 2,
          mx: 0,
        },
        '& table': {
          display: 'block',
          overflowX: 'auto',
          borderCollapse: 'collapse',
          ...theme.typography.body2,
        },
        '& th, & td': {
          border: 1,
          borderColor: 'divider',
          px: 1.5,
          py: 0.75,
          textAlign: 'left',
          verticalAlign: 'top',
        },
        '& th': { bgcolor: 'surfaceContainer', fontWeight: 600 },
        '& hr': { border: 0, borderTop: 1, borderColor: 'divider' },
      })}
    >
      <Markdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </Markdown>
    </Box>
  );
}
